import { Component, Vue, Watch } from 'vue-property-decorator';
import { Logger } from 'fsts';
import { namespace } from 'vuex-class';
import D4yTable from '@/components/d4y-table/d4y-table.vue';
import { EntraJobDetailLog, EntraJobItem, LogAction } from '@/shared/model/entraJobDetailLog';
import entrajobsSearchData, { EntraJobsSearchData } from '@/shared/model/smallPayloadModels/entrajobsSearchData';

const logger = new Logger('entra-jobs-view');
const entraJobsModule = namespace('entraJobs');
const authModule = namespace('auth');

@Component({
  components: { D4yTable },
})
export default class EntraJobs extends Vue {
  @authModule.Getter('getEnvironment')
  private environment!: string;

  get notProduction() {
    return this.environment && !this.environment.toLowerCase().includes('prod');
  }

  @entraJobsModule.Action('getLastJobTime')
  actionGetLastJobTime!: () => Promise<any>;
  @entraJobsModule.Action('syncLocalGroupsAndMembersWithAzure')
  actionSyncLocalGroupsAndMembersWithAzure!: () => Promise<any>;
  @entraJobsModule.Action('getSharepointGroupsMembers')
  actionGetSharepointGroupsMembers!: () => Promise<any>;
  @entraJobsModule.Action('syncSharepointGroups')
  actionSyncSharepointGroups!: (data: any) => Promise<any>;
  @entraJobsModule.Getter('getEntraJobsSearchParams')
  private entraJobsSearchParams!: any;
  @entraJobsModule.Getter('isEntraJobsSearchDataEmpty')
  private isEntraJobsSearchDataEmpty!: any;
  @entraJobsModule.Getter('getEntraJobs')
  private getEntraJobs!: any;
  @entraJobsModule.Getter('getEntraJobsSearchData')
  private getEntraJobsSearchData!: any;
  @entraJobsModule.Action('getEntraJobsFacetSearch')
  private actionGetEntraJobsFacetSearch!: any;
  @entraJobsModule.Action('updateEntraJobsSearchData')
  private actionUpdateEntraJobsSearchData!: any;
  @entraJobsModule.Action('getAllEntraJobs')
  actionGetAllEntraJobs!: () => Promise<any>;
  @entraJobsModule.Action('updateEntraJobInterval')
  actionUpdateEntraJobInterval!: (payload: any) => Promise<any>;

  @entraJobsModule.Action('getJobDetailLogs')
  actionGetJobDetailLogs!: (payload: any) => Promise<any>;

  @entraJobsModule.Action('resetEntraJobsSearchData')
  actionResetEntraJobsSearchData!: () => Promise<any>;

  activeTab: string = 'tabAction';
  jobsList: any[] = [];
  isLoadingLogs: boolean = false;
  detailLogsList: EntraJobDetailLog[] = [];
  datePickerMenu: boolean = false;
  jobsTableList: EntraJobItem[] = [];
  isLoadingJobs: boolean = false;

  logFilters = {
    jobName: null as string | null,
    action: null as string | null,
    entityId: '',
    dateFrom: '',
  };

  actionOptions = Object.values(LogAction);

  logTableHeaders = [
    { text: this.$t('job_name'), value: 'job_name', sortable: true },
    { text: this.$t('entity'), value: 'entity_id', sortable: true },
    { text: this.$t('timestamp'), value: 'timestamp', sortable: true },
    { text: this.$t('action'), value: 'action', sortable: true },
    { text: this.$t('changes'), value: 'changes_json', sortable: false },
  ];

  // Modal JSON preview
  private facetSearch = false;
  private menuDisabled = true;
  private chipData: Array<{ chipText: string; chipField: string; searchWord: string }> = [];
  private searchWord = '';
  private menuItems: any[] = [];
  private loadingFacet = false;

  jobsTableOptions = {
    page: 1,
    itemsPerPage: 10,
  };

  // ==================== MODAL WINDOW FOR JSON ====================
  jsonDialog: boolean = false;
  jsonDialogTitle: string = 'JSON Viewer';
  selectedJsonRaw: string = '';

  updateUsersFromEntraResponse: string = '';
  isUpdatingUsersFromEntra: boolean = false;
  syncLocalContactsWithAzureUsersResponse: string = '';
  isSyncUsersFromDbWithEntra: boolean = false;
  syncLocalGroupsAndMembersWithAzureResponse: string = '';
  isSyncLocalGroupsAndMembersWithEntra: boolean = false;

  entraGroupsWithMembersResponse: string = '';
  isGettingEntraGroupsWithMembers: boolean = false;

  getSharepointGroupsAndMembersResponse: string = '';
  isGettingSharepointGroupAndMembers: boolean = false;
  syncSharepointGroupResponse: string = '';
  isSyncSharepointGroup: boolean = false;

  created() {
    logger.debug('created');
    this.actionGetLastJobTime()
      .then((result) => {
        console.log('zzzzz result.result :>> ', result);
        this.jobsList = result?.data?.result || [];
      })
      .catch((err) => {
        logger.error(err);
      });

    this.loadDetailLogs();
  }

  // Loading logs with filters
  loadDetailLogs() {
    this.isLoadingLogs = true;
    const payload = {
      searchParams: this.entraJobsSearchParams,
      entraJobsSearchData: {
        jobName: this.logFilters.jobName,
        action: this.logFilters.action,
        entity: this.logFilters.entityId?.trim() || null,
        timestamp: this.logFilters.dateFrom || null,
      },
    };

    this.actionGetJobDetailLogs(payload)
      .then((response: any) => {
        this.detailLogsList = response?.data || [];
        this.isLoadingLogs = false;
      })
      .catch((err: any) => {
        logger.error('Failed to load logs', err);
        this.isLoadingLogs = false;
      });
  }

  resetFilters() {
    this.logFilters = {
      jobName: null,
      action: null,
      entityId: '',
      dateFrom: '',
    };
    this.loadDetailLogs();
  }

  // Colors for different action types
  getActionChipColor(action: string | LogAction): string {
    switch (action) {
      case LogAction.Insert:
        return 'green darken-1';
      case LogAction.Update:
        return 'blue darken-1';
      case LogAction.UpdateButWasDelete:
        return 'deep-orange darken-1';
      case LogAction.Delete:
        return 'red darken-1';
      case LogAction.Error:
        return 'red darken-3';
      case LogAction.Warning:
        return 'amber darken-2';
      default:
        return 'grey darken-1';
    }
  }

  formatTimestamp(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  // View JSON
  openJsonViewer(rawContent: string, title: string = 'JSON Viewer') {
    this.jsonDialogTitle = title;
    this.selectedJsonRaw = rawContent;
    this.jsonDialog = true;
  }

  get formattedSelectedJson(): string {
    try {
      const parsed = JSON.parse(this.selectedJsonRaw);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return this.selectedJsonRaw;
    }
  }

  getJobInfo(jobName: string): string {
    const job = this.jobsList?.find((j) => j.job_name === jobName);
    if (!job) return '';

    // Simple friendly date: "Jan 27, 15:55"
    const date = new Date(job.started_at);
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('userTimeZone :>> ', userTimeZone);
    const deDate = date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone,
    });
    const last_run = this.$t('lastrun');
        const by = this.$t('by');
          const took = this.$t('took');
    return `${last_run} ${deDate} ${by} ${job.createdByName} (${took} ${job.durationFormatted})`;
  }

  updateUsersFromEntra() {
    this.isUpdatingUsersFromEntra = true;
    this.$store
      .dispatch('entraJobs/updateUsersFromEntra')
      .then((response) => {
        console.log('Users updated', response);
        this.isUpdatingUsersFromEntra = false;
        this.updateUsersFromEntraResponse = response.data?.result || 'No result returned';
      })
      .catch((error) => {
        this.isUpdatingUsersFromEntra = false;
        console.error('Error updating users', error);
      });
  }

  getEntraGroupsWithMembers() {
    this.isGettingEntraGroupsWithMembers = true;
    this.$store
      .dispatch('entraJobs/getEntraGroupsWithMembers')
      .then((response) => {
        console.log('Users updated', response);
        this.isGettingEntraGroupsWithMembers = false;

        const { result, errors } = response.data || {}; // Destructure response.data safely

        let resultMessage = 'No result returned';

        if (result) {
          resultMessage = `${result}\n`;
        } else if (errors?.length > 0) {
          resultMessage = errors[0]?.description || resultMessage;
        }

        console.log('result :>> ', resultMessage);
        this.entraGroupsWithMembersResponse = resultMessage;
      })
      .catch((error) => {
        this.isGettingEntraGroupsWithMembers = false;
        console.error('Error updating users', error);
      });
  }

  syncLocalContactsWithAzureUsers() {
    this.isSyncUsersFromDbWithEntra = true;
    this.$store
      .dispatch('entraJobs/syncLocalContactsWithAzureUsers')
      .then((response) => {
        console.log('Contacts synced', response);
        this.isSyncUsersFromDbWithEntra = false;

        const { result, errors } = response.data || {}; // Destructure response.data safely

        let resultMessage = 'No result returned';

        if (result) {
          resultMessage = `${result}\n`;
        } else if (errors?.length > 0) {
          resultMessage = errors[0]?.description || resultMessage;
        }

        console.log('result :>> ', resultMessage);
        this.syncLocalContactsWithAzureUsersResponse = resultMessage;
      })
      .catch((error) => {
        this.isSyncUsersFromDbWithEntra = false;
        console.error('Error syncing contacts', error);
      });
  }

  syncLocalGroupsAndMembersWithAzure() {
    this.isSyncLocalGroupsAndMembersWithEntra = true;
    this.$store
      .dispatch('entraJobs/syncLocalGroupsAndMembersWithAzure')
      .then((response) => {
        console.log('Contacts synced', response);
        this.isSyncLocalGroupsAndMembersWithEntra = false;
        const { result, errors } = response.data || {}; // Destructure response.data safely

        let resultMessage = 'No result returned';

        if (result) {
          resultMessage = `${result}\n`;
        } else if (errors?.length > 0) {
          resultMessage = errors[0]?.description || resultMessage;
        }

        console.log('result :>> ', resultMessage);
        this.syncLocalGroupsAndMembersWithAzureResponse = resultMessage;
      })
      .catch((error) => {
        this.isSyncLocalGroupsAndMembersWithEntra = false;
        console.error('Error syncing contacts', error);
      });
  }

  //   getSharepointGroupsAndMembersResponse: string = '';
  // isGettingSharepointGroupAndMembers: boolean = false;

  getSharepointGroupsMembersViaRestApi() {
    this.isGettingSharepointGroupAndMembers = true;
    this.actionGetSharepointGroupsMembers()
      .then((result: any) => {
        this.isGettingSharepointGroupAndMembers = false;
        console.log('zzz result getSharepointGroupsMembersViaRestApi :>> ', result);
      })
      .catch((err: any) => {
        this.isGettingSharepointGroupAndMembers = false;
        console.error('Error getting sharepoint groups and members', err);
      });
  }

  syncSharepointGroup() {
    this.isSyncSharepointGroup = true;

    const payload = {
      isProcessSingleRecord: false,
    };

    this.actionSyncSharepointGroups(payload)
      .then((response) => {
        console.log('Sharepoint members synced', response);
        this.isSyncSharepointGroup = false;
        const { result, errors } = response.data || {}; // Destructure response.data safely

        let resultMessage = 'No result returned';

        if (result) {
          resultMessage = `${result}\n`;
        } else if (errors?.length > 0) {
          resultMessage = errors[0]?.description || resultMessage;
        }

        console.log('result :>> ', resultMessage);
        this.syncSharepointGroupResponse = resultMessage;
      })
      .catch((error) => {
        this.isSyncSharepointGroup = false;
        console.error('Error syncing Sharepoint members', error);
      });
    // setTimeout(() => {
    //   this.isSyncSharepointGroup = false;
    //   this.syncSharepointGroupResponse = 'Error with connection to SharePoint';
    // }, 10000);
  }

  @entraJobsModule.Action('deleteSharepointTestUser')
  actionDeleteSharepointTestUser!: () => Promise<any>;

  isDeletingTestUser = false;
  deleteTestUserInStagingResponse = '';

  deleteTestUserInStaging() {
    this.isDeletingTestUser = true;
    this.actionDeleteSharepointTestUser()
      .then((response) => {
        console.log('Sharepoint test user delete', response);
        this.isDeletingTestUser = false;
        const { result, errors } = response.data || {}; // Destructure response.data safely

        let resultMessage = 'No result returned';

        if (result) {
          resultMessage = `${result}\n`;
        } else if (errors?.length > 0) {
          resultMessage = errors[0]?.description || resultMessage;
        }

        console.log('result :>> ', resultMessage);
        this.deleteTestUserInStagingResponse = resultMessage;
      })
      .catch((error) => {
        this.isDeletingTestUser = false;
        console.error('Error delete Sharepoint test user', error);
      });
  }

  @entraJobsModule.Action('getVmeApiUsers')
  actionGetVmeApiUsers!: () => Promise<any>;
  @entraJobsModule.Action('updateConditionFromVmeApiUsersTable')
  actionUpdateConditionFromVmeApiUsersTable!: () => Promise<any>;

  isGetVmeApiUsers = false;
  getVmeApiUsersResponse = '';
  isUpdateConditionFromVmeApiUsersTable = false;
  updateConditionFromVmeApiUsersTableResponse = '';

  getVmeApiUsers() {
    this.isGetVmeApiUsers = true;
    this.actionGetVmeApiUsers()
      .then((response) => {
        console.log('VME user sync', response);
        this.isGetVmeApiUsers = false;
        const { result, errors } = response.data || {}; // Destructure response.data safely

        let resultMessage = 'No result returned';

        if (result) {
          resultMessage = `${result}\n`;
        } else if (errors?.length > 0) {
          resultMessage = errors[0]?.description || resultMessage;
        }

        console.log('result :>> ', resultMessage);
        this.getVmeApiUsersResponse = resultMessage;
      })
      .catch((error) => {
        this.isGetVmeApiUsers = false;
        console.error('Error syncing VME user', error);
      });
  }

  updateConditionFromVmeApiUsersTable() {
    this.isUpdateConditionFromVmeApiUsersTable = true;
    this.actionUpdateConditionFromVmeApiUsersTable()
      .then((response) => {
        console.log('updateConditionFromVmeApiUsersTable', response);

        this.isUpdateConditionFromVmeApiUsersTable = false;
        const { result, errors } = response.data || {}; // Destructure response.data safely

        // console.log('result :>> ', result);
        // console.log('errors?.length :>> ', errors?.length);
        let resultMessage = 'No result returned';

        if (result) {
          resultMessage = `${result}\n`;
        } else if (errors?.length > 0) {
          resultMessage = errors[0]?.description || resultMessage;
        }

        console.log('result :>> ', resultMessage);
        this.updateConditionFromVmeApiUsersTableResponse = resultMessage;
      })
      .catch((error) => {
        this.isUpdateConditionFromVmeApiUsersTable = false;
        console.error('Error updating condition from VME API users table', error);
        // let resultMessage  = error;

        // const {  response } =  error || {};  // Destructure response.data safely
        // console.log('error response :>> ', response);
        // if (response?.data?.errors && response?.data?.errors?.length > 0) {
        //   resultMessage = response?.data?.errors[0]?.description || resultMessage
        // }

        let resultMessage = error;
        const response =
          error && typeof error === 'object' && 'response' in error ? (error as any).response : undefined;
        console.log('error response :>> ', response);
        if (response?.data?.errors && response.data.errors.length > 0 && response.data.errors[0]?.description) {
          resultMessage = response.data.errors[0].description;
        } else if (error && typeof (error as any).message === 'string') {
          resultMessage = (error as any).message;
        } else {
          resultMessage = String(error);
        }

        this.updateConditionFromVmeApiUsersTableResponse = resultMessage;
      });
  }

  //#region AD-279 ZR Portal Full Scan
  @entraJobsModule.Action('zrPortalFullScanForZrMaster')
  actionZrPortalFullScanForZrMaster!: () => Promise<any>;

  isZrPortalFullScan = false;
  zrPortalFullScanResponse = '';

  zrPortalFullScanForZrMaster() {
    this.isZrPortalFullScan = true;
    this.actionZrPortalFullScanForZrMaster()
      .then((response) => {
        this.isZrPortalFullScan = false;
        const { result, errors } = response.data || {};
        let resultMessage = 'No result returned';
        if (result) {
          resultMessage = `${result}`;
        } else if (errors?.length > 0) {
          resultMessage = errors[0]?.description || resultMessage;
        }
        this.zrPortalFullScanResponse = resultMessage;
      })
      .catch((error) => {
        this.isZrPortalFullScan = false;
        console.error('Error running ZR Portal Full Scan', error);
        this.zrPortalFullScanResponse = error?.message || String(error);
      });
  }
  //#endregion

  //#region AD-270 add `vme_test` users in `tbl_sync_testuser` table
  @entraJobsModule.Action('copyTestUserInSyncTable')
  actionCopyTestUserInSyncTable!: () => Promise<any>;

  isCopyTestUserInSyncTable = false;
  copyTestUserInSyncTableResponse = '';

  copyTestUserInSyncTable() {
    this.isCopyTestUserInSyncTable = true;
    this.actionCopyTestUserInSyncTable()
      .then((response) => {
        console.log('Copy Test User in Sync Table', response);
        this.isCopyTestUserInSyncTable = false;
        const { result, errors } = response.data || {}; // Destructure response.data safely

        let resultMessage = 'No result returned';

        if (result) {
          resultMessage = `${result}\n`;
        } else if (errors?.length > 0) {
          resultMessage = errors[0]?.description || resultMessage;
        }

        console.log('result :>> ', resultMessage);
        this.copyTestUserInSyncTableResponse = resultMessage;
      })
      .catch((error) => {
        this.isCopyTestUserInSyncTable = false;
        console.error('Error copying test user in sync table', error);
      });
  }

  //#endregion

  @Watch('getEntraJobsSearchData', { deep: true })
  public async onSearchDataFilterChanged() {
    if (this.entraJobsSearchParams?.dataOption) {
      this.entraJobsSearchParams.dataOption.page = 1;
    }
    console.log('-----------------getEntraJobsSearchData------------------------------------  ===>>> searchParams  ');
    await this.getEntraJobsData();
  }

  private async getEntraJobsData() {
    this.isLoadingLogs = true;

    try {
      const payload = {
        searchParams: this.entraJobsSearchParams,
        entraJobsSearchData: {
          ...this.getEntraJobsSearchData,
          searchFacetFields: this.entraJobsSearchParams.andClauseFieldsIds,
        },
      };

      await this.actionGetJobDetailLogs(payload);
    } catch (err: any) {
      logger.error('Failed to load entra job detail logs', err);
    } finally {
      this.isLoadingLogs = false;
    }
  }

  get searchMenuObj() {
    return {
      facetSearch: this.facetSearch,
      menuDisabled: this.menuDisabled,
      menuItems: this.menuItems,
      hasResults: this.hasResults,
      chipData: this.chipData,
    };
  }

  get defaultJobsSearchMenuObj() {
    return this.searchMenuObj;
  }

  get hasResults() {
    return this.menuItems.length > 0 && !!this.menuItems[0]?.field;
  }

  get isSearchDataEmpty() {
    return this.isEntraJobsSearchDataEmpty && !this.entraJobsSearchParams.filter && !this.chipData.length;
  }

  get chipDataString() {
    return this.chipData.map((el: any) => `${el.chipField}:${el.searchWord}`).join(',');
  }

  private async selectionUpdateOptions(newVal: any, action: any) {
    if (action == 'search') {
      this.searchFacetModeInEntraJobs(newVal);
    } else if (action == 'reset') {
      this.resetAllFacetOptionsAndSearch();
      this.getEntraJobsData();
    } else {
      if (this.entraJobsSearchParams !== undefined) {
        this.entraJobsSearchParams.dataOption = newVal;
        this.getEntraJobsData();
      }
    }
  }

  private searchFacetModeInEntraJobs(e: any): void {
    const searchWord: string = e.filter;
    let selectedListItemId = document.querySelector('.v-list-item--highlighted .v-list-item__title')?.id;
    if (selectedListItemId != undefined) {
      let menuItem = this.menuItems.find((x: any) => x.field == selectedListItemId);
      this.facetSearch = false;
      this.searchInRealObjects(menuItem);
      return;
    }
    this.searchWord = searchWord;
    // if user press "enter" key again then make search in all fields
    if (this.facetSearch && this.menuItems.length != 0) {
      this.facetSearch = false;
      // return;
    }
    // handle facet search for words more than 2 letters
    if (searchWord?.length >= 2) {
      // const { start, end } = this.getStartEndPeriodForSearch();
      this.loadingFacet = true;
      this.makeFacetSearch(searchWord)
        .then((result: any) => {
          this.formatResultFromSearch(result);
          this.facetSearch = true;
          this.menuDisabled = false;
        })
        .finally(() => {
          this.loadingFacet = false;
        });
    } else if (searchWord?.trim().length === 0) {
      // load all invoices if press `Enter` with clear input
      this.getEntraJobsData();
    }
  }

  formatResultFromSearch(result: any) {
    const fieldsWithResult = Object.keys(result).filter((key) => result[key] > 0);
    this.resetMenuItems();
    fieldsWithResult.forEach((element: string) => {
      const fieldName = element.charAt(0).toUpperCase() + element.slice(1);
      let valueSearch: string = this.searchWord;
      const fieldNameTranslated = this.$i18n.tc(`facet_search.${fieldName}`);
      this.menuItems.push({
        title: `${fieldName}: ${valueSearch} (${result[element]})`,
        titleTranslated: `${fieldNameTranslated}: ${valueSearch} (${result[element]})`,
        fieldTranslated: fieldNameTranslated,
        field: fieldName,
      });
    });
    if (fieldsWithResult.length === 0) {
      this.menuItems.push({
        titleTranslated: this.$i18n.tc('facet_search.no_results'),
      });
      return;
    }
    this.menuItems.unshift({
      titleTranslated: this.$i18n.tc('facet_search.all'),
      field: 'all',
      title: 'All',
    });
  }

  private resetMenuItems() {
    this.menuItems = [];
  }

  private resetChips() {
    this.chipData = [];
  }

  async makeFacetSearch(searchWord: any) {
    let payload = {
      searchWord: searchWord,
      chipDataString: this.chipDataString,
    };

    payload = { ...this.getEntraJobsSearchData, ...payload };
    return this.actionGetEntraJobsFacetSearch(payload);
  }

  private changeSearchInput(value: string) {
    if (value == null || value?.length == 0) {
      this.resetSearch();
    }
  }

  private resetSearch() {
    this.searchWord = '';
    this.entraJobsSearchParams.dataOption.page = 1;
    this.getEntraJobs();
  }

  searchInRealObjects(item: any) {
    this.searchInRealEntraJobs(item);
  }

  searchInRealEntraJobs(item: any) {
    const searchParams = this.entraJobsSearchParams; //this.searchParams;
    let valueSearchWord: string = this.searchWord;
    searchParams.andClauseFieldsIds.push({
      chipField: item.field,
      searchWord: valueSearchWord,
    });

    if (this.searchWord?.length >= 2 && item.title.length > 0) {
      this.actionGetJobDetailLogs({
        searchParams,
        entraJobsSearchData: this.getEntraJobsSearchData,
      })
        .then((result: any) => {
          this.menuDisabled = true;
          if (item.field != 'all') {
            let valueSearch: string = this.searchWord;
            this.chipData.push({
              chipText: `${item.fieldTranslated}: ${valueSearch}`,
              chipField: item.field,
              searchWord: valueSearch,
            });
          }
        })
        .finally(() => {
          this.resetFacetMenu();
        });
    }
  }

  resetFacetMenu() {
    this.menuDisabled = true;
    this.facetSearch = false;
    this.searchWord = '';
  }

  removeChip(chipText: any) {
    const index = this.chipData.findIndex((x) => x.chipText === chipText);
    this.chipData.splice(index, 1);
    const indexOdata = this.entraJobsSearchParams.andClauseFieldsIds.findIndex((x: any) => x.chipText === chipText);
    this.entraJobsSearchParams.andClauseFieldsIds.splice(indexOdata, 1);

    this.menuItems = [];
    this.menuDisabled = true;
    this.getEntraJobsData();
  }

  clearAllFilters() {
    this.actionUpdateEntraJobsSearchData(entrajobsSearchData.defaultData());
    this.resetAllFacetOptionsAndSearch();
  }

  resetAllFacetOptionsAndSearch() {
    this.resetChips();
    this.resetMenuItems();
    this.resetFacetMenu();
    this.entraJobsSearchParams.andClauseFieldsIds = [];
    this.entraJobsSearchParams.filter = '';
  }

  get items() {
    return this.getEntraJobs.items;
  }

  get jobTableHeaders() {
    let headers: {
      text: string | any;
      value: string;
      align?: string;
      class?: string | string[];
      sortable?: boolean;
      width?: string;
    }[] = [
      { text: this.$t('job_name') || 'Job Name', value: 'job_name', sortable: true },
      { text: this.$t('status') || 'Status', value: 'last_status', sortable: true, width: '120px', align: 'center' },
      { text: this.$t('last_run'), value: 'last_started_at', sortable: true, width: '160px' },
      { text: this.$t('last_run_by'), value: 'last_run_by', sortable: true },
      { text: this.$t('last_run_type'), value: 'last_run_type', sortable: false, width: '160px' },
      { text: this.$t('details'), value: 'log_details', sortable: false, width: '160px' },
      { text: this.$t('interval') || 'Intervall', value: 'job_interval', sortable: true, width: '120px' },
      { text: this.$t('description') || 'Beschreibung', value: 'job_description', sortable: true },
    ];
    return headers;
  }

  loadJobsTable() {
    this.isLoadingJobs = true;
    this.actionGetAllEntraJobs()
      .then((response: any) => {
        this.jobsTableList = response?.data?.result || response?.data || response || [];
      })
      .catch((err: any) => {
        logger.error('Failed to load Entra Jobs', err);
      })
      .finally(() => {
        this.isLoadingJobs = false;
      });
  }
  @Watch('activeTab')
  onTabChange(newTab: string) {
    // Guard against parallel requests while fetching fresh data every time tab opens // was `this.jobsTableList.length === 0` which worked on intitial open, but not worked when switching tabs
    if (newTab === 'tabJobs' && !this.isLoadingJobs) {
      this.loadJobsTable();
    }
  }

  getJobStatusChipColor(status: string): string {
    if (!status) return 'grey lighten-1';
    const s = status.toLowerCase();
    if (s.includes('success') || s.includes('finished') || s.includes('ok')) return 'green darken-1';
    if (s.includes('error') || s.includes('fail')) return 'red darken-1';
    if (s.includes('running') || s.includes('progress')) return 'blue darken-1';
    if (s.includes('warn')) return 'amber darken-2';
    return 'blue-grey darken-1';
  }

  // ==================== EDIT JOB INTERVAL (CRON) ====================
  intervalDialog: boolean = false;
  selectedJobForInterval: EntraJobItem | null = null;
  editingIntervalValue: string = '';
  intervalValidationError: string = '';
  isSavingInterval: boolean = false;

  openIntervalDialog(item: EntraJobItem) {
    this.selectedJobForInterval = item;
    this.editingIntervalValue = item.job_interval || '';
    this.intervalValidationError = '';
    this.intervalDialog = true;
  }

  closeIntervalDialog() {
    this.intervalDialog = false;
    this.selectedJobForInterval = null;
    this.editingIntervalValue = '';
    this.intervalValidationError = '';
  }

  validateIntervalInput() {
    const val = (this.editingIntervalValue || '').trim();
    // Allow an empty value (if the user wishes to delete the timetable)
    if (!val) {
      this.intervalValidationError = '';
      return true;
    }

    if (!this.isValidUbuntuCron(val)) {
      this.intervalValidationError = 'Invalid Cron syntax. Expected 5 parts (e.g. "0 22 * * *")';
      return false;
    }

    this.intervalValidationError = '';
    return true;
  }

  isValidUbuntuCron(cronStr: string): boolean {
    const parts = cronStr.trim().split(/\s+/);
    if (parts.length !== 5) return false;

    const ranges = [
      [0, 59], // Minutes  (0 - 59)
      [0, 23], // Hours  (0 - 23)
      [1, 31], // Day of the month (1 - 31)
      [1, 12], // Month  (1 - 12)
      [0, 7], // Day of the week (0–7, where 0 and 7 are Sunday)
    ];

    return parts.every((part, index) => {
      const [min, max] = ranges[index];

      // Split by commas for lists (e.g. ‘1,15,30’)
      return part.split(',').every((segment) => {
        const stepParts = segment.split('/');
        if (stepParts.length > 2) return false;

        // If there is a step (/n), we check that the step is a positive number
        if (stepParts.length === 2) {
          const step = Number(stepParts[1]);
          if (!Number.isInteger(step) || step <= 0 || step > max) return false;
        }

        const rangePart = stepParts[0];
        if (rangePart === '*') return true;

        // Checking a range (e.g. ‘1–5’)
        if (rangePart.includes('-')) {
          const [startStr, endStr] = rangePart.split('-');
          if (!/^\d+$/.test(startStr) || !/^\d+$/.test(endStr)) return false;
          const start = Number(startStr);
          const end = Number(endStr);
          return start >= min && end <= max && start <= end;
        }

        // Normal number
        if (!/^\d+$/.test(rangePart)) return false;
        const num = Number(rangePart);
        return num >= min && num <= max;
      });
    });
  }

  saveJobInterval() {
    if (!this.selectedJobForInterval || !this.validateIntervalInput()) {
      return;
    }

    this.isSavingInterval = true;
    const normalizedInterval = (this.editingIntervalValue || '').trim().replace(/\s+/g, ' ') || null;

    const payload = {
      jobid: (this.selectedJobForInterval as any).id,
      jobname: this.selectedJobForInterval.job_name,
      jobinterval: normalizedInterval,
    };

    this.actionUpdateEntraJobInterval(payload)
      .then(() => {
        // Update the value locally in the table
        if (this.selectedJobForInterval) {
          this.selectedJobForInterval.job_interval = normalizedInterval || '';
        }
        this.closeIntervalDialog();
      })
      .catch((err) => {
        logger.error('Failed to update job interval', err);
        this.intervalValidationError = 'Error saving to server. Please try again.';
      })
      .finally(() => {
        this.isSavingInterval = false;
      });
  }

  getDescriptionValue(description: string | null | undefined): string {
    if (!description) {
      return '';
    }

    return this.$te(description) ? String(this.$t(description)) : '';
  }

  formatText(text: string | null | undefined): string {
    if (!text) {
      return '';
    }
    return this.$te(text) ? String(this.$t(text)) : '';
  }

  getRunType(text: string | null | undefined): string {
    if (!text) {
      return '';
    }
    return this.$te(text) ? String(this.$t(text)) : '';
  }
}
